import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type { Amenity, Booking, BookingChannel } from "../store";
import {
  computeFreeSlots,
  durationOptions,
  findBookingConflict,
  toHHMM,
  toMinutes,
} from "../services/availability";

// ---------------------------------------------------------------------------
// Reusable reservation form. Used by Reservas (staff booker — books on behalf
// of a customer) and by the public Airbnb-style page (self-service). Computes
// the price from the chosen amenity and only offers FREE slots (amenity hours
// − existing reservations) so two bookings can never overlap. Returns a draft
// booking to the caller, which runs the confirm flow (invoice + calendar +
// email).
// ---------------------------------------------------------------------------

export type BookingDraft = Omit<Booking, "id" | "invoiceNumber"> & {
  /** Trampa para bots (campo oculto): una persona lo deja vacío. */
  website?: string;
};

interface Props {
  amenities: Amenity[];
  /** All existing bookings — used to compute free slots & block overlaps. */
  bookings: Booking[];
  channel: BookingChannel;
  themeClasses: ThemeClasses;
  /** Pre-selected amenity (e.g. when booking from a public listing card). */
  initialAmenityId?: string;
  /** Name of the agent creating a staff booking (stamped on the booking). */
  agentName?: string;
  onCancel: () => void;
  onSubmit: (draft: BookingDraft) => void;
  /** Standalone (public) renders its own overlay; admin embeds inside Modal. */
  embedded?: boolean;
}

export default function BookingFormModal({
  amenities,
  bookings,
  channel,
  themeClasses: tc,
  initialAmenityId,
  agentName,
  onCancel,
  onSubmit,
  embedded = false,
}: Props) {
  const { t } = useTranslation("common");
  const bookable = amenities.filter((a) => a.status === "Disponible");
  const [amenityId, setAmenityId] = useState(
    initialAmenityId ?? bookable[0]?.id ?? "",
  );
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [date, setDate] = useState("");
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const amenity = amenities.find((a) => a.id === amenityId);
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.bg} ${tc.text}`;

  // Duration choices for the chosen amenity (min..max in min steps).
  const durations = useMemo(
    () => (amenity ? durationOptions(amenity) : []),
    [amenity],
  );

  // Keep duration valid whenever the amenity changes.
  useEffect(() => {
    if (durations.length && !durations.includes(duration)) {
      setDuration(durations[0]);
    }
  }, [durations, duration]);

  // Free start-slots = amenity hours − existing reservations for that day.
  const freeSlots = useMemo(() => {
    if (!amenity || !date || !duration) return [];
    return computeFreeSlots({
      amenity,
      bookings,
      date,
      durationMinutes: duration,
    });
  }, [amenity, bookings, date, duration]);

  // Drop a chosen start that is no longer free (amenity/date/duration changed).
  useEffect(() => {
    if (startTime && !freeSlots.some((s) => s.start === startTime)) {
      setStartTime("");
    }
  }, [freeSlots, startTime]);

  const endTime = startTime ? toHHMM(toMinutes(startTime) + duration) : "";

  const submit = () => {
    if (!amenity || !customerName.trim() || !date || !startTime || !duration) {
      setError(t("basesPropiedades.booking.required"));
      return;
    }
    // Defense in depth: even though only free slots are offered, re-check that
    // nothing overlaps before committing (acceptance: no double-booking).
    const conflict = findBookingConflict(
      bookings,
      amenity.id,
      date,
      startTime,
      endTime,
    );
    if (conflict) {
      setError(
        t("basesPropiedades.booking.overlap", {
          time: `${conflict.startTime}–${conflict.endTime}`,
        }),
      );
      return;
    }
    const price = amenity.price ?? 0;
    onSubmit({
      amenityId: amenity.id,
      amenityName: amenity.name,
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      date,
      startTime,
      endTime,
      durationMinutes: duration,
      channel,
      agentName: channel === "staff" ? agentName : undefined,
      status: "Confirmada",
      price,
      paymentStatus: price > 0 ? "Pendiente" : "No requerido",
      notes: notes.trim() || undefined,
      ...(channel === "public" ? { website } : {}),
    });
  };

  const form = (
    <div className="space-y-4">
      <label className="block">
        <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
          {t("basesPropiedades.fields.amenity")}
        </span>
        <select
          className={inputCls}
          value={amenityId}
          onChange={(e) => {
            setAmenityId(e.target.value);
            setStartTime("");
          }}
        >
          {bookable.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.price > 0 ? ` — $${a.price} ${a.currency}` : ""}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
            {t("basesPropiedades.fields.customer")}
          </span>
          <input
            className={inputCls}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder={
              channel === "staff"
                ? t("basesPropiedades.booking.customerPlaceholder")
                : ""
            }
          />
        </label>
        <label className="block">
          <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
            {t("basesPropiedades.fields.email")}
          </span>
          <input
            type="email"
            className={inputCls}
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
          />
        </label>
        {channel === "public" ? (
          // Trampa para bots: fuera de la vista y del tabulador.
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="absolute -left-[9999px] h-0 w-0 opacity-0"
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <label className="block">
          <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
            {t("basesPropiedades.fields.date")}
          </span>
          <input
            type="date"
            className={inputCls}
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setStartTime("");
            }}
          />
        </label>
        <label className="block">
          <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
            {t("basesPropiedades.fields.duration")}
          </span>
          <select
            className={inputCls}
            value={duration}
            onChange={(e) => {
              setDuration(Number(e.target.value));
              setStartTime("");
            }}
          >
            {durations.map((d) => (
              <option key={d} value={d}>
                {d} min
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
            {t("basesPropiedades.fields.start")}
          </span>
          <select
            className={inputCls}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={!date || freeSlots.length === 0}
          >
            <option value="">
              {!date
                ? t("basesPropiedades.booking.pickDate")
                : freeSlots.length === 0
                  ? t("basesPropiedades.booking.noSlots")
                  : t("basesPropiedades.booking.pickSlot")}
            </option>
            {freeSlots.map((s) => (
              <option key={s.start} value={s.start}>
                {s.start}–{s.end}
              </option>
            ))}
          </select>
        </label>
      </div>

      {date && amenity && freeSlots.length === 0 && (
        <p className="text-xs text-amber-500">
          <i className="ri-information-line mr-1" />
          {t("basesPropiedades.booking.noSlotsHint")}
        </p>
      )}

      <label className="block">
        <span className={`mb-1 block text-sm font-medium ${tc.text}`}>
          {t("basesPropiedades.fields.notes")}
        </span>
        <textarea
          className={inputCls}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {amenity && (
        <div className={`rounded-lg p-3 text-sm ${tc.cardBg} border ${tc.border}`}>
          <div className="flex items-center justify-between">
            <span className={tc.textSecondary}>
              {t("basesPropiedades.booking.total")}
            </span>
            <span className="font-semibold text-primary-500">
              {amenity.price > 0
                ? `$${amenity.price.toFixed(2)} ${amenity.currency}`
                : t("basesPropiedades.free")}
            </span>
          </div>
          {amenity.rules && (
            <p className={`mt-2 text-xs ${tc.textSecondary}`}>
              <i className="ri-information-line mr-1" />
              {amenity.rules}
            </p>
          )}
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}
        >
          {t("basesPropiedades.cancel")}
        </button>
        <button
          onClick={submit}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
        >
          {amenity && amenity.price > 0
            ? t("basesPropiedades.booking.payAndConfirm")
            : t("basesPropiedades.booking.confirm")}
        </button>
      </div>
    </div>
  );

  if (!embedded) return form;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        className={`max-h-[95vh] w-full max-w-xl overflow-y-auto rounded-2xl border p-6 shadow-2xl ${tc.border} ${tc.bg}`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className={`text-lg font-bold ${tc.text}`}>
            {t("basesPropiedades.booking.title")}
          </h2>
          <button onClick={onCancel} className={tc.text}>
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        {form}
      </div>
    </div>
  );
}
